import { useMutation } from "@apollo/client/react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import FlashContext from "components/FlashMessages/FlashContext";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useCanTestTemplateDeployment from "shared/Templates/access/useCanTestTemplateDeployment";
import { TemplateVersion, BlueprintState, Template } from "types/generated";
import { UPDATE_VERSION } from "views/Account/Template/Versions/gql";
import TemplateFeatureGateTooltip from "views/Account/TemplatesWorkbench/FeatureGate/Tooltip";
import { TemplateActions } from "views/Account/TemplatesWorkbench/FeatureGate/types";

import { INITIAL_TEMPLATE } from "../constants";
import BlueprintEditor from "../Editor";
import { VALIDATE_BLUEPRINT_VERSION_TEMPLATE } from "../gql";
import { showPublishConfirmationModal } from "../PublishConfirmationModal";
import styles from "./styles.module.css";

type VersionTemplateBodyProps = {
  version: TemplateVersion;
  versionSchema: string;
  template: Template;
};

const VersionTemplateBody = ({ version, versionSchema, template }: VersionTemplateBodyProps) => {
  const [editorBody, setEditorBody] = useState<string>(INITIAL_TEMPLATE);
  const navigate = useNavigate();
  const canTestDeploy = useCanTestTemplateDeployment();
  const { reportSuccess, onError } = useTypedContext(FlashContext);
  const [errors, setErrors] = useState<string[]>([]);

  const [validateTemplate] = useMutation(VALIDATE_BLUEPRINT_VERSION_TEMPLATE);

  const editorHasUnsavedChanges = version.rawTemplate
    ? editorBody !== version.rawTemplate
    : editorBody !== INITIAL_TEMPLATE;

  useBlockNavigationModal({
    title: "Leave confirmation",
    description: "Are you sure you want to leave? You have unsubmitted changes.",
    isNavigationBlocked: editorHasUnsavedChanges,
  });

  const [updateBlueprint, { loading: saveLoading }] = useMutation(UPDATE_VERSION, {
    refetchQueries: ["GetTemplateVersion"],
  });

  const handleUpdateBlueprint = useCallback(
    (template: string, publish?: boolean) => {
      if (version) {
        updateBlueprint({
          variables: {
            id: version.id,
            input: {
              labels: version.labels,
              state: publish ? BlueprintState.Published : version.state,
              template: template,
              versionNumber: version.version as string,
              instructions: version.instructions,
            },
          },
        })
          .then(({ data }) => {
            if (data?.templateVersionUpdate?.name) {
              reportSuccess({
                message: `Version "${data.templateVersionUpdate.name}" successfully saved`,
              });
            }
          })
          .catch(onError);
      }
    },
    [version, updateBlueprint, reportSuccess, onError]
  );

  const handleValidateTemplate = useCallback(
    (template: string) => {
      setErrors([]);

      return validateTemplate({
        variables: {
          template,
        },
      })
        .then(({ data }) => {
          setErrors(data?.templateVersionParseTemplate?.errors || []);

          return {
            inputs: data?.templateVersionParseTemplate?.inputs || [],
            errors: data?.templateVersionParseTemplate?.errors || [],
          };
        })
        .catch(onError);
    },
    [onError, validateTemplate]
  );

  const handleSaveTemplate = () => {
    handleUpdateBlueprint(editorBody);
  };

  const handleEditorChange = (value?: string) => {
    setEditorBody(value || "");
  };

  useEffect(() => {
    if (version?.rawTemplate) {
      setEditorBody(version.rawTemplate);
      handleValidateTemplate(version.rawTemplate);
    } else {
      handleValidateTemplate(editorBody);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version?.rawTemplate]);

  useTitle(`Template body · ${version?.name}`);

  const isPublished = version.state === BlueprintState.Published;

  const handleOpenTestDeployDrawer = () => {
    navigate(`/template/${template.id}/version/${version.id}/test-deploy`);
  };

  const onPublishConfirmation = () => {
    showPublishConfirmationModal({
      onPublish: () => handleUpdateBlueprint(editorBody, true),
    });
  };

  return (
    <>
      <Box direction="column" grow="1" fullWidth relative>
        <PageInfoHeader title="Template body">
          {!isPublished && (
            <>
              <TemplateFeatureGateTooltip
                action={TemplateActions.EditTemplate}
                template={template}
                disabledMessage={canTestDeploy.result ? undefined : canTestDeploy.message}
                on={({ isDisabled, ...props }) => (
                  <Button
                    {...props}
                    variant="secondary"
                    size="medium"
                    onPress={handleOpenTestDeployDrawer}
                    disabled={isDisabled}
                  >
                    Test deployment
                  </Button>
                )}
              />
              <TemplateFeatureGateTooltip
                action={TemplateActions.EditTemplate}
                template={template}
                on={({ isDisabled, ...props }) => (
                  <Button
                    {...props}
                    variant="secondary"
                    size="medium"
                    onPress={handleSaveTemplate}
                    disabled={saveLoading || isDisabled || !editorHasUnsavedChanges}
                    loading={saveLoading}
                  >
                    Save
                  </Button>
                )}
              />

              <TemplateFeatureGateTooltip
                action={TemplateActions.EditTemplate}
                template={template}
                on={({ isDisabled, ...props }) => (
                  <Button
                    {...props}
                    variant="primary"
                    size="medium"
                    onPress={onPublishConfirmation}
                    disabled={isDisabled || errors.length > 0}
                  >
                    Publish
                  </Button>
                )}
              />
            </>
          )}
        </PageInfoHeader>

        <Box direction="column" grow="1" fullWidth relative className={styles.editorWrapper}>
          <BlueprintEditor
            blueprint={version}
            validate={handleValidateTemplate}
            body={editorBody}
            schema={versionSchema}
            onChange={handleEditorChange}
            readOnly={isPublished || version.deleted}
          />
        </Box>
      </Box>
    </>
  );
};

export default VersionTemplateBody;
